// features/intelligence/components/section-card.js
// ═══════════════════════════════════════════════════════════
// SECTION CARD — outer chrome for any Dashboard section
// header (label + live tag + time) → body (children) →
// optional Practitioner Lens at the bottom.
//
// Phase E (2026-04-19) — added `freshness` prop. When the
// parent passes `freshness={D._freshness?.X}`, the card
// auto-renders the right Live badge + timestamp:
//   - { source: 'live', isStale: false } → sage "Live" + "Updated 7:42 AM"
//   - { source: 'live', isStale: true }  → muted "Stale · last 6:30 AM"
//   - { source: 'sample' } or null       → muted "Sample data"
//
// `live` and `time` props still work for sections that don't
// have freshness wired yet — backwards compatible.
// ═══════════════════════════════════════════════════════════

import PractitionerLens from './practitioner-lens';

export default function SectionCard({
  label,
  live,              // boolean — legacy, used when freshness not provided
  time,              // string — legacy, used when freshness not provided
  freshness,         // { source, fetchedAt, isStale } — preferred
  bodyClass,
  bodyStyle,
  children,
  lens,
  onLensPathway,
}) {
  const { showLive, timeText } = computeStatus({ freshness, live, time });

  return (
    <div className="di-section">
      <div className="di-sec-header">
        <div className="di-sec-label">{label}</div>
        <div className="di-sec-right">
          {showLive && <div className="di-sec-live">Live</div>}
          {timeText && <div className="di-sec-time">{timeText}</div>}
        </div>
      </div>
      <div className={bodyClass || 'di-sec-body'} style={bodyStyle}>
        {children}
      </div>
      <PractitionerLens lens={lens} onPathway={onLensPathway} />
    </div>
  );
}

function computeStatus({ freshness, live, time }) {
  // No freshness data — fall back to the legacy props
  if (!freshness) return { showLive: !!live, timeText: time || '' };

  if (freshness.source === 'sample') {
    return { showLive: false, timeText: 'Sample data' };
  }
  if (freshness.source === 'live') {
    if (freshness.isStale) {
      return {
        showLive: false,
        timeText: freshness.fetchedAt
          ? `Stale · last ${formatTime(freshness.fetchedAt)}`
          : 'Stale',
      };
    }
    return {
      showLive: true,
      timeText: freshness.fetchedAt
        ? `Updated ${formatTime(freshness.fetchedAt)}`
        : '',
    };
  }
  return { showLive: !!live, timeText: time || '' };
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_e) { return ''; }
}
